import { useState } from "react";

import {
  IgButton,
  IgFullPageDialog,
  IgResizableDivider,
  IgTypography,
  useResizablePanel,
} from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useSaveExperience } from "@src/api/experiences/put-experience";
import { AgenticLogicTakeoverHost } from "@src/features/ai/pages/agentic-logic/embedded/AgenticLogicTakeoverHost";
import { useIsAgenticLogicTakeoverActive } from "@src/features/ai/pages/agentic-logic/embedded/takeoverEntry";
import { VivChat } from "@src/features/experienceBuilder/ai/VivChat";
import { useVivChatBridge } from "@src/features/experienceBuilder/ai/useVivChatBridge";
import { useDialog } from "@src/hooks/dialog";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction,
  ExperienceBuilderSelector,
} from "@src/store/slices/experienceBuilder";
import {
  ExperienceBuilderAiAction,
  ExperienceBuilderAiSelector,
} from "@src/store/slices/experienceBuilder/ai";
import { selectedExperience } from "@src/store/slices/experiences";
import { hasUnsavedExperience } from "@src/store/slices/experiences/selectors/hasUnsavedExperience";
import { useKeyPress } from "@src/utils/useKeyPress";

import { ConfirmDialog } from "./dialogs/ConfirmDialog";
import { MultiImageSelectDialog } from "./dialogs/MultiImageSelectDialog";
import { OnsiteUpsellProductPickerDialog } from "./dialogs/OnsiteUpsellProductPickerDialog";
import { ModificationsPanel } from "./modifications/ModificationsPanel";
import { PreviewToolbar } from "./toolbar/PreviewToolbar";
import { ExperienceBuilderPreview } from "./ExperienceBuilderPreview";
import { VivIdeaLibrary } from "@src/features/experienceBuilder/ai/ideaLibrary/VivIdeaLibrary";

const SIDE_PANEL_INITIAL_WIDTH = 360;
const ONSITE_UPSELL_SIDE_PANEL_WIDTH = 550;
const SIDE_PANEL_MAX_WIDTH = 640;
const SIDE_PANEL_MIN_WIDTH = 320;

export const ExperienceBuilderDialog = () => {
  const dispatch = useDispatch();
  const dialog = useDialog("experienceBuilder");

  const theme = useTheme();
  const experience = useSelector(selectedExperience);
  const isOnsiteUpsellBuilder = useSelector(
    ExperienceBuilderSelector.isOnsiteUpsellBuilder
  );
  const hasBuilderChanges = useSelector(
    ExperienceBuilderSelector.hasBuilderChanges
  );
  const hasUnsavedTestChanges = useSelector(hasUnsavedExperience);
  const { saveExperience, isSaving } = useSaveExperience();
  const modificationName = dialog.props?.modificationName;
  const isAiChatOpen = useSelector(ExperienceBuilderAiSelector.isOpen);

  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);

  const leftPanel = useResizablePanel({
    initialSize: isOnsiteUpsellBuilder
      ? ONSITE_UPSELL_SIDE_PANEL_WIDTH
      : SIDE_PANEL_INITIAL_WIDTH,
    minSize: SIDE_PANEL_MIN_WIDTH,
    maxSize: SIDE_PANEL_MAX_WIDTH,
    direction: "horizontal",
    position: "left",
  });

  const aiChatPanel = useResizablePanel({
    initialSize: SIDE_PANEL_INITIAL_WIDTH,
    minSize: SIDE_PANEL_MIN_WIDTH,
    maxSize: SIDE_PANEL_MAX_WIDTH,
    direction: "horizontal",
    position: "right",
  });

  useVivChatBridge();

  const toggleAiChat = (opened: boolean) =>
    dispatch(ExperienceBuilderAiAction.setOpen(opened));
  const close = () => dispatch(ExperienceBuilderAction.closeBuilder());

  const ruleTakeoverActive = useIsAgenticLogicTakeoverActive();
  const componentDraft = useSelector(
    ExperienceBuilderSelector.component.getVariationDraft
  );
  // The rule the takeover created binds to the component being edited the
  // moment it exists — a draft edit like any other, applied with the panel.
  const bindCreatedRule = (agenticLogicRuleId: string) => {
    if (!componentDraft || componentDraft.type !== "onsiteUpsell") return;
    dispatch(
      ExperienceBuilderAction.component.setDraft({
        ...componentDraft,
        agenticLogicRuleId,
      })
    );
  };

  // "Save" persists the whole test, not just the builder state, and leaves the
  // builder open so editing can continue — "Exit" is the way out.
  const save = () => {
    void dispatch(ExperienceBuilderAction.saveBuilder());
    void saveExperience()?.catch(() => {});
  };

  useKeyPress("Escape", () => {
    // Escape belongs to the builder's own draft. While the rule builder owns
    // the content it owns the key too — cancelling a draft the merchant cannot
    // see is not what Escape means there.
    if (dialog.isOpen && !ruleTakeoverActive) {
      void dispatch(ExperienceBuilderAction.cancelDraft());
    }
  });

  const header = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        borderBottom: `1px solid ${theme.igBorders.core.default}`,
        flexShrink: 0,
      }}
    >
      <Stack sx={{ flex: 1 }}>
        <IgTypography variant="body1" fontWeight={600}>
          {experience.name}
        </IgTypography>
        <IgTypography variant="body2" color={theme.palette.text.secondary}>
          {modificationName}
        </IgTypography>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <IgButton variant="text" onClick={close}>
          Exit
        </IgButton>
        <IgButton
          variant="contained"
          color="primary"
          onClick={save}
          disabled={!hasBuilderChanges && !hasUnsavedTestChanges}
          loading={isSaving}
        >
          Save
        </IgButton>
      </Stack>
    </Stack>
  );

  const body = (
    <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
      {isLeftPanelOpen && (
        <>
          <Box
            sx={{
              width: leftPanel.size,
              flexShrink: 0,
              backgroundColor: theme.igBackgroundColors.baseLightHigh,
              overflow: "hidden",
            }}
          >
            <ModificationsPanel />
          </Box>
          <IgResizableDivider
            direction="horizontal"
            onMouseDown={leftPanel.handleMouseDown}
            isResizing={leftPanel.isResizing}
          />
        </>
      )}
      <ExperienceBuilderPreview />
    </Stack>
  );

  // A rule builder opened from an onsite upsell's Rule picker takes the whole
  // builder over. The canvas behind it is hidden rather than unmounted: it
  // holds the storefront preview iframe and the draft the merchant is editing.
  const content = (
    <>
      {ruleTakeoverActive ? (
        <AgenticLogicTakeoverHost
          title="Upsell builder"
          backLabel="Back to upsell settings"
          // The experience builder edits onsite components only; a checkout or
          // post-purchase offer is authored in its own dialog.
          placementSurface="onsite"
          onRuleCreated={bindCreatedRule}
        />
      ) : null}
      <Stack
        direction="row"
        sx={{
          display: ruleTakeoverActive ? "none" : "flex",
          height: "100%",
          width: "100%",
        }}
      >
        <Stack sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          {header}
          <PreviewToolbar
            isLeftPanelOpen={isLeftPanelOpen}
            onToggleLeftPanel={() => setIsLeftPanelOpen((v) => !v)}
          />
          {body}
        </Stack>
        {isAiChatOpen && (
          <>
            <IgResizableDivider
              direction="horizontal"
              onMouseDown={aiChatPanel.handleMouseDown}
              isResizing={aiChatPanel.isResizing}
            />
            <Stack
              sx={{
                width: aiChatPanel.size,
                flexShrink: 0,
                height: "100%",
                backgroundColor: theme.igBackgroundColors.baseLightHigh,
              }}
            >
              <VivChat onClose={() => toggleAiChat(false)} />
              <VivIdeaLibrary />
            </Stack>
          </>
        )}
      </Stack>
    </>
  );

  return (
    <>
      <IgFullPageDialog
        showDialog={dialog.isOpen}
        handleClose={close}
        disableEscapeKeyDown
        content={content}
        disableContentPadding
        hideActions
      />
      <ConfirmDialog />
      <MultiImageSelectDialog />
      <OnsiteUpsellProductPickerDialog />
    </>
  );
};
